<template>
    <div class="setting-page-content-wrapper copilot-prompts-page">
        <div class="page-header">
            <div class="title">{{ $t("copilotPrompts.pageTitle") }}</div>
            <div class="subtitle">
                {{ $t("copilotPrompts.pageSubtitle") }}
            </div>
        </div>

        <div v-for="section in sections" :key="section.key" class="cps">
            <div class="cps__head">
                <i class="material-icons">{{ section.icon }}</i>
                <span>{{ $t(section.labelKey) }}</span>
            </div>
            <div class="cps__grid">
                <div
                    v-for="def in section.items"
                    :key="def.key"
                    class="cps__card"
                    @click="openEditor(def)"
                >
                    <div class="cps__card-icon">
                        <i class="material-icons">{{ def.icon }}</i>
                    </div>
                    <div class="cps__card-text">
                        <span class="cps__card-title">{{ $t(def.labelKey) }}</span>
                        <span class="cps__card-desc">{{ $t(def.descriptionKey) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <Modal2
            v-if="editModal.visible"
            design="v3"
            :large="true"
            :showFooter="false"
            :showTitle="false"
            @cancel="closeEditor"
        >
            <div class="cpm">
                <div class="cpm__header">
                    <div class="cpm__header-icon">
                        <i class="material-icons">{{ editModal.def.icon }}</i>
                    </div>
                    <div>
                        <div class="cpm__header-title">{{ $t(editModal.def.labelKey) }}</div>
                        <div class="cpm__header-desc">{{ $t(editModal.def.descriptionKey) }}</div>
                    </div>
                </div>

                <div class="cpm__body">
                    <div class="cpm__editor-pane">
                        <div class="cpm__editor-label">{{ $t("copilotPrompts.systemPromptLabel") }}</div>
                        <CoPilotPromptEditor
                            ref="promptEditor"
                            :value="editModal.value"
                            @input="onEditorInput"
                        />
                    </div>
                    <div v-if="currentTree.length" class="cpm__attr-pane">
                        <div class="cpm__attr-title">
                            <i class="material-icons">data_object</i>
                            {{ $t("copilotPrompts.variablesTitle") }}
                        </div>
                        <div class="cpm__attr-hint">{{ $t("copilotPrompts.variablesHint") }}</div>
                        <div class="cpm__attr-tree">
                            <div
                                v-for="group in currentTree"
                                :key="group.key"
                                class="cpm__grp"
                            >
                                <div class="cpm__grp-head" @click="toggleGroup(group.key)">
                                    <i class="material-icons cpm__grp-arrow">{{ openGroups[group.key] ? 'expand_more' : 'chevron_right' }}</i>
                                    <i v-if="group.icon" class="material-icons cpm__grp-icon">{{ group.icon }}</i>
                                    <span>{{ group.label }}</span>
                                </div>
                                <div v-if="openGroups[group.key]" class="cpm__grp-items">
                                    <div
                                        v-for="item in group.children"
                                        :key="'item-' + item.key"
                                    >
                                        <a-tooltip
                                            v-if="!item.children"
                                            :title="item.description"
                                            placement="left"
                                        >
                                            <div class="cpm__leaf" @click="insertAttr(item)">
                                                <span class="cpm__leaf-name">{{ item.label }}</span>
                                            </div>
                                        </a-tooltip>
                                        <div v-else class="cpm__sub">
                                            <div class="cpm__sub-head" @click="toggleGroup(item.key)">
                                                <i class="material-icons cpm__grp-arrow">{{ openGroups[item.key] ? 'expand_more' : 'chevron_right' }}</i>
                                                <i v-if="item.icon" class="material-icons cpm__sub-icon">{{ item.icon }}</i>
                                                <span>{{ item.label }}</span>
                                            </div>
                                            <div v-if="openGroups[item.key]" class="cpm__sub-items">
                                                <a-tooltip
                                                    v-for="child in item.children"
                                                    :key="child.key"
                                                    :title="child.description"
                                                    placement="left"
                                                >
                                                    <div class="cpm__leaf" @click="insertAttr(child)">
                                                        <span class="cpm__leaf-name">{{ child.label }}</span>
                                                    </div>
                                                </a-tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="cpm__footer">
                    <div style="flex:1"></div>
                    <SupsisButton :ghost="true" @click="closeEditor">{{ $t("copilotPrompts.cancel") }}</SupsisButton>
                    <SupsisButton :primary="true" @click="savePrompt" :disabled="saving">
                        <i class="material-icons" style="font-size: 15px">save</i>
                        {{ saving ? $t("copilotPrompts.saving") : $t("copilotPrompts.save") }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>
    </div>
</template>

<script>
import { mapState } from "vuex";
import SupsisButton from "../../components/SupsisButton.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import CoPilotPromptEditor from "./CoPilotPromptEditor.vue";
import { buildAttributeTree, getAttributeShortLabel } from "./copilotAttributes";

const CHAT_CATEGORIES = ["settings", "visitor", "user"];
const MAIL_CATEGORIES = ["settings", "user"];

const PROMPT_DEFINITIONS = [
    {
        key: "fixGrammar",
        icon: "spellcheck",
        labelKey: "copilotPrompts.prompt_fixGrammar_label",
        descriptionKey: "copilotPrompts.prompt_fixGrammar_desc",
        group: "chat",
        categories: CHAT_CATEGORIES,
        inputKeys: ["message"],
    },
    {
        key: "rewriteAsLongSentence",
        icon: "text_increase",
        labelKey: "copilotPrompts.prompt_rewriteAsLongSentence_label",
        descriptionKey: "copilotPrompts.prompt_rewriteAsLongSentence_desc",
        group: "chat",
        categories: CHAT_CATEGORIES,
        inputKeys: ["message"],
    },
    {
        key: "changeTone",
        icon: "record_voice_over",
        labelKey: "copilotPrompts.prompt_changeTone_label",
        descriptionKey: "copilotPrompts.prompt_changeTone_desc",
        group: "chat",
        categories: CHAT_CATEGORIES,
        inputKeys: ["tone", "message"],
    },
    {
        key: "messageTranslation",
        icon: "translate",
        labelKey: "copilotPrompts.prompt_messageTranslation_label",
        descriptionKey: "copilotPrompts.prompt_messageTranslation_desc",
        group: "chat",
        categories: CHAT_CATEGORIES,
        inputKeys: ["languageCode", "message"],
    },
    {
        key: "promptToGenerateResponse",
        icon: "auto_awesome",
        labelKey: "copilotPrompts.prompt_promptToGenerateResponse_label",
        descriptionKey: "copilotPrompts.prompt_promptToGenerateResponse_desc",
        group: "chat",
        categories: CHAT_CATEGORIES,
        inputKeys: ["message"],
    },
    {
        key: "mail.fixGrammar",
        icon: "spellcheck",
        labelKey: "copilotPrompts.prompt_mail_fixGrammar_label",
        descriptionKey: "copilotPrompts.prompt_mail_fixGrammar_desc",
        group: "mail",
        categories: MAIL_CATEGORIES,
        inputKeys: ["message"],
    },
    {
        key: "mail.rewriteAsLongSentence",
        icon: "text_increase",
        labelKey: "copilotPrompts.prompt_mail_rewriteAsLongSentence_label",
        descriptionKey: "copilotPrompts.prompt_mail_rewriteAsLongSentence_desc",
        group: "mail",
        categories: MAIL_CATEGORIES,
        inputKeys: ["message"],
    },
    {
        key: "mail.changeTone",
        icon: "record_voice_over",
        labelKey: "copilotPrompts.prompt_mail_changeTone_label",
        descriptionKey: "copilotPrompts.prompt_mail_changeTone_desc",
        group: "mail",
        categories: MAIL_CATEGORIES,
        inputKeys: ["tone", "message"],
    },
    {
        key: "mail.messageTranslation",
        icon: "translate",
        labelKey: "copilotPrompts.prompt_mail_messageTranslation_label",
        descriptionKey: "copilotPrompts.prompt_mail_messageTranslation_desc",
        group: "mail",
        categories: MAIL_CATEGORIES,
        inputKeys: ["languageCode", "message"],
    },
    {
        key: "mailAiResponse",
        icon: "mark_email_read",
        labelKey: "copilotPrompts.prompt_mailAiResponse_label",
        descriptionKey: "copilotPrompts.prompt_mailAiResponse_desc",
        group: "mail",
        categories: MAIL_CATEGORIES,
        inputKeys: ["mailDetails"],
    },
    {
        key: "mailPromtMessage",
        icon: "forward_to_inbox",
        labelKey: "copilotPrompts.prompt_mailPromtMessage_label",
        descriptionKey: "copilotPrompts.prompt_mailPromtMessage_desc",
        group: "mail",
        categories: MAIL_CATEGORIES,
        inputKeys: ["mailDetails", "message"],
    },
];

const SECTIONS = [
    { key: "chat", labelKey: "copilotPrompts.sectionChatAssistant", icon: "chat" },
    { key: "mail", labelKey: "copilotPrompts.sectionMailAssistant", icon: "email" },
];

export default {
    name: "CoPilotPrompts",
    components: { SupsisButton, Modal2, CoPilotPromptEditor },
    props: { saveCopilotPrompts: { type: Function, required: true } },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
        }),
        sections() {
            return SECTIONS.map((s) => ({
                ...s,
                items: PROMPT_DEFINITIONS.filter((d) => d.group === s.key),
            })).filter((s) => s.items.length > 0);
        },
        currentTree() {
            if (!this.editModal.def) return [];
            return buildAttributeTree(this.editModal.def.categories || [], this.editModal.def.inputKeys || []);
        },
    },
    data() {
        return {
            localPrompts: {},
            saving: false,
            openGroups: {},
            editModal: { visible: false, def: null, value: "" },
        };
    },
    created() { this.initLocalPrompts(); },
    methods: {
        initLocalPrompts() {
            const cp = (this.settings && this.settings.copilotPrompts) || {};
            const prompts = {};
            PROMPT_DEFINITIONS.forEach((def) => {
                prompts[def.key] = cp[def.key] || "";
            });
            this.localPrompts = prompts;
        },
        openEditor(def) {
            this.editModal = {
                visible: true,
                def,
                value: this.localPrompts[def.key] || "",
            };
            this.openGroups = {};
        },
        closeEditor() {
            this.editModal = { visible: false, def: null, value: "" };
        },
        onEditorInput(val) { this.editModal.value = val; },
        toggleGroup(key) { this.$set(this.openGroups, key, !this.openGroups[key]); },
        insertAttr(item) {
            const editor = this.$refs.promptEditor;
            if (editor) editor.insertAttribute(item.key, getAttributeShortLabel(item.key));
        },
        savePrompt() {
            const self = this;
            if (!self.editModal.def) return;
            self.saving = true;
            self.$set(self.localPrompts, self.editModal.def.key, self.editModal.value);
            const payload = {};
            const editedKey = self.editModal.def.key;
            payload[editedKey] = self.localPrompts[editedKey];
            self.saveCopilotPrompts(payload)
                .then(function () {
                    self.closeEditor();
                    self.$message.success({ content: self.$t("copilotPrompts.saveSuccess"), duration: 2 });
                })
                .catch(function () {
                    self.$message.error({ content: self.$t("copilotPrompts.saveFailed"), duration: 2 });
                })
                .then(function () {
                    self.saving = false;
                });
        },
    },
    watch: { settings: { deep: true, handler() { this.initLocalPrompts(); } } },
};
</script>

<style scoped lang="scss">
.copilot-prompts-page {
    width: 100%;
    min-height: 120px;
    flex: 1 0 auto;

    .cps {
        margin-bottom: 28px;

        &__head {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            font-weight: 600;
            color: #4b5563;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 12px;

            .material-icons {
                font-size: 18px;
                color: #7c3aed;
                background: linear-gradient(145deg, #ede9fe 0%, #ddd6fe 100%);
                border-radius: 8px;
                padding: 4px;
            }
        }

        &__grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
            gap: 10px;
        }

        &__card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 14px 16px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #ffffff;
            cursor: pointer;
            transition: all 0.15s;
            user-select: none;
            position: relative;

            &:hover {
                border-color: #c4b5fd;
                background: #fafafa;
                box-shadow: 0 2px 12px rgba(91, 33, 182, 0.1);
            }

            &--custom { border-left: 3px solid #7c3aed; }

            &-icon {
                width: 38px;
                height: 38px;
                border-radius: 10px;
                background: linear-gradient(160deg, #e9d5ff 0%, #ddd6fe 55%, #c4b5fd 100%);
                border: 1px solid rgba(124, 58, 237, 0.25);
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;

                .material-icons { font-size: 20px; color: #5b21b6; }
            }

            &-text {
                flex: 1;
                min-width: 0;
                display: flex;
                flex-direction: column;
            }

            &-title {
                font-size: 13px;
                font-weight: 600;
                color: #1e1b4b;
            }

            &-desc {
                font-size: 11px;
                color: #7c7c9a;
                margin-top: 2px;
                line-height: 1.3;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            &-badge {
                position: absolute;
                top: 6px;
                right: 8px;
                font-size: 9px;
                font-weight: 700;
                padding: 1px 6px;
                background: linear-gradient(180deg, #ede9fe 0%, #ddd6fe 100%);
                color: #6d28d9;
                border-radius: 6px;
                border: 1px solid #a78bfa;
                text-transform: uppercase;
                letter-spacing: 0.3px;
            }
        }
    }
}
</style>

<style lang="scss">
.cpm {
    display: flex;
    flex-direction: column;
    min-height: 500px;

    &__header {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 24px 28px 18px;

        &-icon {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            background: linear-gradient(160deg, #e9d5ff 0%, #ddd6fe 55%, #c4b5fd 100%);
            border: 1px solid rgba(124, 58, 237, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            .material-icons { font-size: 24px; color: #5b21b6; }
        }

        &-title { font-size: 18px; font-weight: 700; color: #1e1b4b; }
        &-desc { font-size: 13px; color: #7c7c9a; margin-top: 2px; }
        &-badge {
            margin-left: auto;
            font-size: 11px;
            font-weight: 600;
            padding: 3px 10px;
            background: linear-gradient(180deg, #ede9fe 0%, #ddd6fe 100%);
            color: #6d28d9;
            border-radius: 8px;
            border: 1px solid #a78bfa;
        }
    }

    &__body {
        display: flex;
        flex: 1;
        padding: 0 28px;
        gap: 20px;
        min-height: 0;
    }

    &__editor-pane {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;

        .cpe { flex: 1; }
    }

    &__editor-label {
        font-size: 11px;
        font-weight: 600;
        color: #7c7c9a;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        margin-bottom: 8px;
    }

    &__attr-pane {
        width: 220px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        border-left: 1px solid #e5e7eb;
        padding-left: 20px;
    }

    &__attr-title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 700;
        color: #4b5563;
        margin-bottom: 2px;

        .material-icons {
            font-size: 16px;
            color: #7c3aed;
            background: linear-gradient(145deg, #ede9fe 0%, #ddd6fe 100%);
            border-radius: 6px;
            padding: 3px;
        }
    }

    &__attr-hint {
        font-size: 10px;
        color: #a5a5c0;
        margin-bottom: 10px;
    }

    &__attr-tree {
        flex: 1;
        overflow-y: auto;
        max-height: 380px;
    }

    &__grp {
        margin-bottom: 2px;

        &-head {
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 5px 4px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            font-weight: 600;
            color: #1e1b4b;
            user-select: none;

            &:hover { background: #f3e8ff; }
        }

        &-arrow { font-size: 16px; color: #a5a5c0; }
        &-icon { font-size: 15px; color: #6d28d9; }
        &-items { padding-left: 10px; }
    }

    &__sub {
        &-head {
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 4px 4px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 11px;
            font-weight: 500;
            color: #4a4a6a;
            user-select: none;

            &:hover { background: #f3e8ff; }
        }

        &-icon { font-size: 14px; color: #7c3aed; }
        &-items { padding-left: 14px; }
    }

    &__leaf {
        padding: 4px 8px;
        border-radius: 6px;
        cursor: pointer;
        transition: background 0.1s;

        &:hover { background: #ede9fe; box-shadow: inset 0 0 0 1px #ddd6fe; }

        &-name {
            font-size: 12px;
            color: #1e1b4b;
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 16px 28px;
        border-top: 1px solid #e5e7eb;
    }
}

.dark-mode .cpm {
    &__header-title { color: #ede9fe; }
    &__header-desc { color: #a5a5c0; }
    &__editor-label { color: #a5a5c0; }
    &__attr-pane { border-left-color: #3f3f46; }
    &__grp-head { color: #ede9fe; &:hover { background: #3f2d5c; } }
    &__sub-head { color: #c4b5fd; &:hover { background: #3f2d5c; } }
    &__leaf { &:hover { background: #3f2d5c; } &-name { color: #ede9fe; } }
    &__footer { border-top-color: #3f3f46; }
}
</style>
